import type { Token } from "@solomon-apps/shared";
import type { JupiterTokenSearchResult } from "@solomon-labs/swap-client";

/**
 * Resolve a swap leg's token from its mint.
 *
 * The token picker offers every token Jupiter can route, but the session token
 * list only knows the handful Solomon ships logos and rules for. Session tokens
 * win; any other mint falls back to Jupiter's token metadata. `/order` returns
 * raw amounts only, so a mint is unusable (`undefined`) until its decimals are
 * known: never guess them, since a wrong guess misprices the swap.
 */
export function resolveSwapToken(
    mint: string,
    sessionTokens: Token[],
    metadata: JupiterTokenSearchResult[]
): Token | undefined {
    if (!mint) return undefined;

    const sessionToken = sessionTokens.find((token) => token.address === mint);
    if (sessionToken) return sessionToken;

    const meta = metadata.find((token) => token.id === mint);
    if (!meta || typeof meta.decimals !== "number" || !Number.isInteger(meta.decimals) || meta.decimals < 0) {
        return undefined;
    }

    const symbol = meta.symbol || `${mint.slice(0, 4)}…`;
    return {
        address: mint,
        name: meta.name || symbol,
        symbol,
        decimals: meta.decimals,
        uri: "",
        logo: jupiterLogoUrl(meta.icon) ?? "",
        types: []
    };
}

// Public IPFS gateways that now refuse direct image requests (ipfs.io answers
// every request with 429 and "switching to a service worker gateway only").
const RETIRED_IPFS_GATEWAYS = ["ipfs.io", "dweb.link", "cloudflare-ipfs.com", "nftstorage.link", "w3s.link"];
const IPFS_GATEWAY = "https://gateway.pinata.cloud/ipfs/";

function ipfsPath(url: URL): string | null {
    if (url.protocol === "ipfs:") return `${url.hostname}${url.pathname === "/" ? "" : url.pathname}`;
    const subdomain = RETIRED_IPFS_GATEWAYS.map((host) => `.ipfs.${host}`).find((suffix) =>
        url.hostname.endsWith(suffix)
    );
    if (subdomain) {
        const path = url.pathname === "/" ? "" : url.pathname;
        return `${url.hostname.slice(0, -subdomain.length)}${path}`;
    }
    if (RETIRED_IPFS_GATEWAYS.includes(url.hostname) && url.pathname.startsWith("/ipfs/")) {
        return url.pathname.slice("/ipfs/".length);
    }
    return null;
}

/**
 * Load a Jupiter token logo the way jup.ag does: retired IPFS gateways move to
 * Pinata, then everything goes through the wsrv.nl image cache at 32px@2x.
 * The URL matches jup.ag's byte for byte, so it usually hits wsrv's warm cache.
 * Local assets, data URIs and non-https URLs are returned unchanged.
 */
export function jupiterLogoUrl(icon: string | undefined): string | undefined {
    if (!icon) return icon;
    let url: URL;
    try {
        url = new URL(icon);
    } catch {
        return icon;
    }
    const path = ipfsPath(url);
    if (path === null && url.protocol !== "https:") return icon;
    const source = path === null ? icon : `${IPFS_GATEWAY}${path}`;
    return `https://wsrv.nl/?w=32&h=32&url=${encodeURIComponent(source)}&dpr=2&quality=80`;
}
